import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DiffEditorModel } from 'ngx-monaco-editor-v2';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import beautify from 'xml-beautifier';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from '../services/common.service';
import { RuleEngineService } from '../services/rule-engine.service';
import { W700servicesService } from '../services/w700services.service';

export type CompareSectionKey =
  | 'transform'
  | 'validation'
  | 'delivery'
  | 'rule'
  | 'vaRules'
  | 'lookups';
export type CompareEditorKey = 'transform' | 'validation' | 'delivery' | 'rule' | 'vaRules';

/** Legacy validationconfig XML vs RouteRuleMapping / RuleDefinition (PreValidation). */
export type ValidationMappingMode = 'legacy' | 'rules' | 'none';

export type RuleExecutionStage = 'Delivered' | 'Validation' | 'PreValidation';

/** Metadata from GetMappings/Get for SaveTransformConfig / SaveValidationConfig. */
export interface CompareMappingSaveMeta {
  template: string;
  resultjson: string;
  version: string;
  coreConfigVersionList: any[];
  liveVersion: string;
}

/** How version dropdowns are sourced for a compare pane. */
export type CompareVersionSource = 'core' | 'rules' | 'none';

/** Processor on a multi-processor route (from api/Routes/get RouteProcessorData). */
export interface CompareRouteProcessor {
  id: string;
  name: string;
  displayname: string;
  apikey: string;
  sequence: number;
  isLive: boolean;
}

export interface MappingLoadResult {
  displayXml: string;
  rawXml: string;
  meta: CompareMappingSaveMeta;
  validationMode?: ValidationMappingMode;
  versionList?: any[];
  selectedVersion?: string;
  versionSource?: CompareVersionSource;
}

interface MappingLoadError {
  error: string;
}

export type ComparePaneSide = 'left' | 'right';

interface XmlMappingSaveResult {
  side: ComparePaneSide;
  message: string;
  meta: CompareMappingSaveMeta;
}

type ValidationSaveResult =
  | XmlMappingSaveResult
  | { side: ComparePaneSide; message: string; refreshedContent?: string };

export interface CompareLookupRow {
  id: number;
  contextId: string;
  lookupType: string;
  lookupId: string;
  lookupValue: string;
  selected: boolean;
}

export interface CompareSectionState {
  expanded: boolean;
  loaded: boolean;
  loading: boolean;
  editing: boolean;
  saving: boolean;
  showEditor: boolean;
  error: string | null;
  leftContent: string;
  rightContent: string;
  leftContentRaw: string;
  rightContentRaw: string;
  /** Snapshot at load / edit start for change detection on save. */
  leftBackup: string;
  rightBackup: string;
  leftSaveMeta: CompareMappingSaveMeta | null;
  rightSaveMeta: CompareMappingSaveMeta | null;
  leftLookups: CompareLookupRow[];
  rightLookups: CompareLookupRow[];
  /** @deprecated Both sides may be selected; kept for clear/reset compatibility. */
  lookupSelectionSide: ComparePaneSide | null;
  lookupSyncing: boolean;
  /** Per-pane validation source: legacy XML mapping or pre-validation rules JSON. */
  leftValidationMode: ValidationMappingMode;
  rightValidationMode: ValidationMappingMode;
  /** Per-pane version dropdowns (Transform / Validation / Pre-Delivery). */
  leftVersionList: any[];
  rightVersionList: any[];
  leftVersionValue: string;
  rightVersionValue: string;
  leftVersionSource: CompareVersionSource;
  rightVersionSource: CompareVersionSource;
}

@Component({
  selector: 'app-interface-compare',
  templateUrl: './interface-compare.component.html',
  styleUrls: ['./interface-compare.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterfaceCompareComponent implements OnInit {
  @Input() inputDataInModalComponent: { left: any; right: any };

  leftInterface: any;
  rightInterface: any;

  /** Route processors for multi-processor compare (Transform / legacy Validation). */
  leftProcessors: CompareRouteProcessor[] = [];
  rightProcessors: CompareRouteProcessor[] = [];
  leftSelectedProcessorId = '';
  rightSelectedProcessorId = '';
  /** Last processor id that mapping was loaded for (avoids ngModel/selectionChange race). */
  private leftLoadedProcessorId = '';
  private rightLoadedProcessorId = '';

  sections: Record<CompareSectionKey, CompareSectionState> = {
    transform: this.createSectionState(false),
    validation: this.createSectionState(false),
    delivery: this.createSectionState(false),
    rule: this.createSectionState(false),
    vaRules: this.createSectionState(false),
    lookups: this.createSectionState(false),
  };

  readonly lookupHelpTooltip =
    'Insert: select row(s) on one side only, then use the arrow toward the other side.\n\n' +
    'Update: select the same number of rows on both sides (selected rows stay at the top; 1st↔1st, 2nd↔2nd…). Review the pairs, then confirm.\n\n' +
    'Rows are unique by Lookup Type + Lookup Id + Lookup Value — duplicates are skipped.\n\n' +
    'Highlighted rows: that Type + Id + Value combination is missing on the other side.';

  /** Pending position-paired update awaiting user confirm. */
  lookupUpdatePreview: {
    sourceSide: ComparePaneSide;
    targetSide: ComparePaneSide;
    pairs: Array<{ source: CompareLookupRow; target: CompareLookupRow }>;
  } | null = null;

  transformOldModel: DiffEditorModel = { code: '', language: 'xml' };
  transformNewModel: DiffEditorModel = { code: '', language: 'xml' };
  validationOldModel: DiffEditorModel = { code: '', language: 'xml' };
  validationNewModel: DiffEditorModel = { code: '', language: 'xml' };
  deliveryOldModel: DiffEditorModel = { code: '', language: 'json' };
  deliveryNewModel: DiffEditorModel = { code: '', language: 'json' };
  ruleOldModel: DiffEditorModel = { code: '', language: 'json' };
  ruleNewModel: DiffEditorModel = { code: '', language: 'json' };
  vaRulesOldModel: DiffEditorModel = { code: '', language: 'json' };
  vaRulesNewModel: DiffEditorModel = { code: '', language: 'json' };

  private diffEditors: Partial<Record<CompareEditorKey, any>> = {};

  private readonly monacoPerfOptions = {
    minimap: { enabled: false },
    renderOverviewRuler: false,
    automaticLayout: true,
  };

  transformDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'xml',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  validationDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'xml',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  deliveryDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'json',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  ruleDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'json',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  vaRulesDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'json',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  constructor(
    private activeModal: NgbActiveModal,
    private service: CommonService,
    private ruleEngineService: RuleEngineService,
    private w700Service: W700servicesService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.inputDataInModalComponent?.left || !this.inputDataInModalComponent?.right) {
      this.toastr.error('Compare data is missing.', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
      this.close();
      return;
    }
    this.leftInterface = this.inputDataInModalComponent.left;
    this.rightInterface = this.inputDataInModalComponent.right;
    this.initRouteProcessors();
  }

  private initRouteProcessors(): void {
    forkJoin({
      left: this.fetchRouteProcessors$(this.leftInterface),
      right: this.fetchRouteProcessors$(this.rightInterface),
    }).subscribe({
      next: (result) => {
        this.leftProcessors = result.left;
        this.rightProcessors = result.right;
        this.leftSelectedProcessorId = this.pickDefaultProcessorId(
          this.leftProcessors,
          this.leftInterface,
        );
        this.rightSelectedProcessorId = this.pickDefaultProcessorId(
          this.rightProcessors,
          this.rightInterface,
        );
        this.leftLoadedProcessorId = this.leftSelectedProcessorId;
        this.rightLoadedProcessorId = this.rightSelectedProcessorId;
        this.cdr.markForCheck();
      },
    });
  }

  private fetchRouteProcessors$(iface: any): Observable<CompareRouteProcessor[]> {
    const routeId = iface?.id;
    if (!routeId) {
      return of(this.fallbackProcessorsFromIface(iface));
    }
    return this.service.getapiurl('api/Routes/get?id=' + encodeURIComponent(routeId)).pipe(
      map((data: any) => this.normalizeRouteProcessors(data?.RouteProcessorData, iface)),
      catchError(() => of(this.fallbackProcessorsFromIface(iface))),
    );
  }

  private normalizeRouteProcessors(raw: any, iface: any): CompareRouteProcessor[] {
    const list = Array.isArray(raw) ? raw : [];
    if (!list.length) {
      return this.fallbackProcessorsFromIface(iface);
    }
    const normalized = list
      .map((p: any, idx: number) => ({
        id: String(p?.id ?? '').trim(),
        name: String(p?.name ?? '').trim(),
        displayname: String(p?.displayname ?? p?.name ?? '').trim(),
        apikey: String(p?.apikey ?? '').trim(),
        sequence: Number(p?.Sequence ?? p?.sequence ?? idx + 1) || idx + 1,
        isLive: p?.ISLIVE === true || p?.islive === true,
      }))
      .filter((p) => !!p.id)
      .sort((a, b) => a.sequence - b.sequence);
    return normalized.length ? normalized : this.fallbackProcessorsFromIface(iface);
  }

  private fallbackProcessorsFromIface(iface: any): CompareRouteProcessor[] {
    const id = String(iface?.PAID ?? '').trim();
    if (!id) {
      return [];
    }
    return [
      {
        id,
        name: String(iface?.PANAME ?? '').trim(),
        displayname: String(iface?.PANAME ?? '').trim(),
        apikey: String(iface?.PAAPIKEY ?? '').trim(),
        sequence: 1,
        isLive: true,
      },
    ];
  }

  private pickDefaultProcessorId(list: CompareRouteProcessor[], iface: any): string {
    if (!list.length) {
      return '';
    }
    const fromIface = String(iface?.PAID ?? '').trim();
    const match = list.find((p) => p.id === fromIface);
    if (match) {
      return match.id;
    }
    const live = list.find((p) => p.isLive);
    return (live ?? list[0]).id;
  }

  showProcessorSelect(side: ComparePaneSide): boolean {
    const list = side === 'left' ? this.leftProcessors : this.rightProcessors;
    return list.length > 1;
  }

  supportsProcessorCompare(key: CompareSectionKey): boolean {
    return key === 'transform' || key === 'validation';
  }

  getProcessors(side: ComparePaneSide): CompareRouteProcessor[] {
    return side === 'left' ? this.leftProcessors : this.rightProcessors;
  }

  getSelectedProcessorId(side: ComparePaneSide): string {
    return side === 'left' ? this.leftSelectedProcessorId : this.rightSelectedProcessorId;
  }

  /** Interface row with PAID/PAAPIKEY overridden by the selected compare processor. */
  private withSelectedProcessor(iface: any, side: ComparePaneSide): any {
    const list = this.getProcessors(side);
    const selectedId = this.getSelectedProcessorId(side);
    const proc = list.find((p) => p.id === selectedId);
    if (!proc) {
      return iface;
    }
    return {
      ...iface,
      PAID: proc.id,
      PAAPIKEY: proc.apikey,
      PANAME: proc.displayname || proc.name,
    };
  }

  /** Resolves a display field from list-row properties (casing varies). */
  displayField(iface: any, keys: string[]): string {
    if (!iface) {
      return '—';
    }
    for (const key of keys) {
      const val = iface[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val);
      }
    }
    return '—';
  }

  /** Matches interface list: active_card vs failed_card (status === 'active'). */
  isInterfaceActive(iface: any): boolean {
    return String(iface?.status ?? '').toLowerCase() === 'active';
  }

  private createSectionState(expanded: boolean): CompareSectionState {
    return {
      expanded,
      loaded: false,
      loading: false,
      editing: false,
      saving: false,
      showEditor: false,
      error: null,
      leftContent: '',
      rightContent: '',
      leftContentRaw: '',
      rightContentRaw: '',
      leftBackup: '',
      rightBackup: '',
      leftSaveMeta: null,
      rightSaveMeta: null,
      leftLookups: [],
      rightLookups: [],
      lookupSelectionSide: null,
      lookupSyncing: false,
      leftValidationMode: 'legacy',
      rightValidationMode: 'legacy',
      leftVersionList: [],
      rightVersionList: [],
      leftVersionValue: '',
      rightVersionValue: '',
      leftVersionSource: 'none',
      rightVersionSource: 'none',
    };
  }

  /** Sections that support Live/QC/Dev version compare dropdowns. */
  supportsVersionCompare(key: CompareSectionKey): boolean {
    return key === 'transform' || key === 'validation' || key === 'delivery' || key === 'rule';
  }

  showVersionToolbar(key: CompareSectionKey): boolean {
    if (!this.supportsVersionCompare(key)) {
      return false;
    }
    const section = this.sections[key];
    if (!section.loaded) {
      return false;
    }
    const hasVersions =
      section.leftVersionList.length > 0 || section.rightVersionList.length > 0;
    const hasProcessors =
      this.supportsProcessorCompare(key) &&
      (this.showProcessorSelect('left') || this.showProcessorSelect('right'));
    return hasVersions || hasProcessors;
  }

  onDiffEditorInit(key: CompareEditorKey, editor: any): void {
    this.diffEditors[key] = editor;
  }

  isEditableSection(key: CompareSectionKey): boolean {
    return key !== 'lookups' && key !== 'vaRules';
  }

  hasDiffEditor(key: CompareSectionKey): boolean {
    return key !== 'lookups';
  }

  canEditSection(key: CompareSectionKey): boolean {
    const section = this.sections[key];
    if (!this.isEditableSection(key) || !section.loaded || section.loading) {
      return false;
    }
    return !this.isInterfaceLocked(this.leftInterface) || !this.isInterfaceLocked(this.rightInterface);
  }

  isInterfaceLocked(iface: any): boolean {
    const raw = iface?.IsLocked ?? iface?.isLocked ?? false;
    return String(raw).toLowerCase() === 'true' || raw === true;
  }

  isSideEditable(side: ComparePaneSide): boolean {
    const iface = side === 'left' ? this.leftInterface : this.rightInterface;
    return !this.isInterfaceLocked(iface);
  }

  /** Hide Rule + VA rules when both compared interfaces are inbound. */
  showRuleCompareSections(): boolean {
    return !(
      this.isInboundInterface(this.leftInterface) &&
      this.isInboundInterface(this.rightInterface)
    );
  }

  private isInboundInterface(iface: any): boolean {
    if (!iface) {
      return false;
    }
    const direction = String(iface?.Direction ?? iface?.direction ?? '').toLowerCase();
    const interfaceType = String(
      iface?.interfacetype ?? iface?.INTERFACETYPE ?? '',
    ).toLowerCase();
    return direction.includes('inbound') || interfaceType.includes('inbound');
  }

  /** Edit is only available while the section is expanded (and not already editing). */
  isEditDisabled(key: CompareSectionKey): boolean {
    const section = this.sections[key];
    return (
      !section.expanded ||
      !section.loaded ||
      section.loading ||
      section.editing ||
      section.saving ||
      !this.canEditSection(key)
    );
  }

  isCancelEditDisabled(key: CompareEditorKey): boolean {
    const section = this.sections[key];
    return !section.editing || section.saving;
  }

  private getSectionDisplayName(key: CompareSectionKey): string {
    switch (key) {
      case 'transform':
        return 'Transformation mapping';
      case 'validation':
        return 'Validation mapping';
      case 'delivery':
        return 'Pre-Delivery rules';
      case 'rule':
        return 'Rule';
      case 'vaRules':
        return 'VA rules';
      default:
        return 'Section';
    }
  }

  private getSaveSuccessMessage(side: ComparePaneSide, key: CompareSectionKey): string {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    return `${sideLabel} ${this.getSectionDisplayName(key)} saved successfully`;
  }

  private getSaveFailureMessage(
    side: ComparePaneSide,
    key: CompareSectionKey,
    message: string,
  ): string {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    const detail = (message || '').trim() || 'Save failed.';
    return `${sideLabel} ${this.getSectionDisplayName(key)}: ${detail}`;
  }

  private showSaveSuccessToasts(results: { side: ComparePaneSide }[], key: CompareSectionKey): void {
    results.forEach((r) => {
      this.toastr.success(this.getSaveSuccessMessage(r.side, key), '', {
        timeOut: 2000,
        positionClass: 'toast-bottom-right',
      });
    });
  }

  private showSaveFailureToasts(
    failures: Array<{ side: ComparePaneSide; message: string }>,
    key: CompareSectionKey,
  ): void {
    failures.forEach((f) => {
      const msg = this.getSaveFailureMessage(f.side, key, f.message);
      if ((f.message || '') === 'Invalid characters in rulename.') {
        this.toastr.warning(msg, '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
        return;
      }
      this.service.copyableToastError(msg);
    });
  }

  /**
   * Prevents forkJoin from aborting when one side fails so both success and failure
   * toasts can be shown (left/right, including multi-processor saves).
   */
  private asSideSaveOutcome$<T extends { side: ComparePaneSide }>(
    save$: Observable<T>,
    side: ComparePaneSide,
    fallbackMessage: string,
  ): Observable<{ ok: true; result: T } | { ok: false; side: ComparePaneSide; message: string }> {
    return save$.pipe(
      map((result) => ({ ok: true as const, result })),
      catchError((err) =>
        of({
          ok: false as const,
          side,
          message: err?.message || fallbackMessage,
        }),
      ),
    );
  }

  /** Pipeline audit — contextId is route UniqueID (same as create.component). */
  private recordPipelineSaveAudit(
    uniqueId: string,
    actionType: string,
    newValue: string,
    description: string,
    oldValue = '',
  ): void {
    if (!uniqueId) {
      return;
    }
    this.service
      .auditsave(actionType, oldValue, newValue, 'Pipeline', uniqueId, description)
      .then(() => {})
      .catch(() => {});
  }

  /** Compare save always results in Dev; audit shows version number + Dev only. */
  private formatCompareMappingAuditVersionLabel(
    meta: CompareMappingSaveMeta | null,
    versionToSave: string,
  ): string {
    return `${this.resolveAuditVersionNumber(meta, versionToSave)} Dev`;
  }

  private resolveAuditVersionNumber(
    meta: CompareMappingSaveMeta | null,
    versionToSave: string,
  ): string {
    const trimmed = String(versionToSave ?? '').trim();
    if (trimmed !== '') {
      return trimmed;
    }
    const list = meta?.coreConfigVersionList ?? [];
    let max = 0;
    for (const entry of list) {
      const n = parseInt(String(entry?.version ?? ''), 10);
      if (!isNaN(n) && n > max) {
        max = n;
      }
    }
    return String(max > 0 ? max + 1 : 1);
  }

  private buildMappingSaveAudit(
    meta: CompareMappingSaveMeta | null,
    versionToSave: string,
    key: CompareEditorKey,
    side: ComparePaneSide,
  ): { actionType: string; newValue: string; description: string } {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    const sectionName = this.getSectionDisplayName(key);
    const versionString = this.formatCompareMappingAuditVersionLabel(meta, versionToSave);
    const isUpdate = String(versionToSave ?? '').trim() !== '';

    if (isUpdate) {
      return {
        actionType: 'Update',
        newValue: `Compare ${sideLabel} ${sectionName}: Mapping version is updated- ${versionString}`,
        description: 'Mapping Version Updated Successfully',
      };
    }
    return {
      actionType: 'Create',
      newValue: `Compare ${sideLabel} ${sectionName}: New Mapping version is Created- ${versionString}`,
      description: 'Mapping Version Created Successfully',
    };
  }

  private buildRuleSaveAudit(
    stage: RuleExecutionStage,
    key: CompareEditorKey,
    side: ComparePaneSide,
    targetVersion: number,
    targetStatus: string,
    isNewRuleConfigVersion: boolean,
  ): { actionType: string; newValue: string; description: string } {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    const sectionName = this.getSectionDisplayName(key);
    const auditLabel = this.resolveRuleAuditLabel(stage);
    const prefix = `Compare ${sideLabel} ${sectionName}: `;

    if (isNewRuleConfigVersion) {
      return {
        actionType: 'Create',
        newValue: `${prefix}New ${auditLabel} version is Created- ${targetVersion} ${targetStatus}`,
        description: `${auditLabel} Version Created Successfully.`,
      };
    }
    return {
      actionType: 'Update',
      newValue: `${prefix}${auditLabel} version is updated- ${targetVersion} ${targetStatus}`,
      description: `${auditLabel} Version Updated Successfully`,
    };
  }

  getSectionLockNote(key: CompareSectionKey): string | null {
    const leftLocked = this.isInterfaceLocked(this.leftInterface);
    const rightLocked = this.isInterfaceLocked(this.rightInterface);
    if (leftLocked && rightLocked) {
      return 'Both interfaces are locked. Editing is disabled.';
    }
    if (leftLocked) {
      return 'Left interface is locked. Left pane is read-only.';
    }
    if (rightLocked) {
      return 'Right interface is locked. Right pane is read-only.';
    }
    return null;
  }

  /** Notes when left/right validation use different storage (legacy XML vs pre-validation rules). */
  getValidationFormatNote(): string | null {
    const section = this.sections.validation;
    if (!section.loaded) {
      return null;
    }
    const leftMode = section.leftValidationMode;
    const rightMode = section.rightValidationMode;
    if (leftMode === 'none' && rightMode === 'none') {
      return null;
    }
    const leftLabel = this.getValidationModeLabel(leftMode);
    const rightLabel = this.getValidationModeLabel(rightMode);
    if (leftMode === rightMode) {
      return `Both sides use ${leftLabel}.`;
    }
    return `Left: ${leftLabel}. Right: ${rightLabel}.`;
  }

  private getValidationModeLabel(mode: ValidationMappingMode): string {
    if (mode === 'rules') {
      return 'pre-validation rules (JSON)';
    }
    if (mode === 'none') {
      return 'no validation mapping';
    }
    return 'legacy validation mapping (XML)';
  }

  toggleSection(key: CompareSectionKey): void {
    const section = this.sections[key];
    section.expanded = !section.expanded;
    if (!section.expanded) {
      section.showEditor = false;
      if (key === 'lookups') {
        this.clearLookupSelections(section);
        this.lookupUpdatePreview = null;
      }
      if (this.hasDiffEditor(key)) {
        this.clearDiffEditor(key as CompareEditorKey);
        if (this.isEditableSection(key)) {
          this.cancelSectionEdit(key as CompareEditorKey);
        }
      }
    } else if (this.hasDiffEditor(key) && section.loaded && !section.loading) {
      this.scheduleEditorMount(key as CompareEditorKey);
    }
    if (section.expanded && !section.loaded && !section.loading) {
      this.loadSection(key);
    }
    this.cdr.markForCheck();
  }

  /** Exit edit mode when section is collapsed (unsaved edits discarded for display). */
  private clearDiffEditor(key: CompareEditorKey): void {
    delete this.diffEditors[key];
  }

  private cancelSectionEdit(key: CompareEditorKey): void {
    const section = this.sections[key];
    if (!section.editing) {
      this.setSectionEditing(key, false);
      return;
    }
    section.editing = false;
    this.setSectionEditing(key, false);
    if (section.loaded) {
      section.leftContent = section.leftBackup;
      section.rightContent = section.rightBackup;
      this.applyDiffModels(key, section.leftContent, section.rightContent);
    }
  }

  cancelEditSection(key: CompareEditorKey): void {
    const section = this.sections[key];
    if (!section.editing || section.saving) {
      return;
    }
    this.cancelSectionEdit(key);
    if (section.expanded && section.loaded) {
      this.scheduleEditorMount(key);
    }
    this.cdr.markForCheck();
  }

  loadSection(key: CompareSectionKey): void {
    const section = this.sections[key];
    if (section.loading) {
      return;
    }
    section.loading = true;
    section.error = null;
    this.cdr.markForCheck();

    if (key === 'lookups') {
      this.loadLookupsSection(section);
      return;
    }

    const fetch$ = this.getMappingFetcher(key);

    forkJoin({
      left: fetch$(this.withSelectedProcessor(this.leftInterface, 'left')).pipe(
        catchError((err) => of({ error: this.errorMessage(err) })),
      ),
      right: fetch$(this.withSelectedProcessor(this.rightInterface, 'right')).pipe(
        catchError((err) => of({ error: this.errorMessage(err) })),
      ),
    }).subscribe({
      next: (result) => {
        section.loading = false;

        const leftErr = this.isMappingLoadError(result.left) ? result.left.error : null;
        const rightErr = this.isMappingLoadError(result.right) ? result.right.error : null;
        if (leftErr || rightErr) {
          section.error = [leftErr, rightErr].filter(Boolean).join(' | ');
        }

        if (!leftErr && this.isMappingLoadResult(result.left)) {
          const left = result.left;
          section.leftContent = left.displayXml;
          section.leftContentRaw = left.rawXml;
          section.leftBackup = left.displayXml;
          section.leftSaveMeta = left.meta;
          this.applyVersionStateFromLoad(section, 'left', left);
          if (key === 'validation') {
            section.leftValidationMode = left.validationMode ?? 'legacy';
          }
        } else {
          section.leftContent = '';
          section.leftContentRaw = '';
          section.leftBackup = '';
          section.leftSaveMeta = null;
          this.clearVersionState(section, 'left');
          if (key === 'validation') {
            section.leftValidationMode = 'none';
          }
        }

        if (!rightErr && this.isMappingLoadResult(result.right)) {
          const right = result.right;
          section.rightContent = right.displayXml;
          section.rightContentRaw = right.rawXml;
          section.rightBackup = right.displayXml;
          section.rightSaveMeta = right.meta;
          this.applyVersionStateFromLoad(section, 'right', right);
          if (key === 'validation') {
            section.rightValidationMode = right.validationMode ?? 'legacy';
          }
        } else {
          section.rightContent = '';
          section.rightContentRaw = '';
          section.rightBackup = '';
          section.rightSaveMeta = null;
          this.clearVersionState(section, 'right');
          if (key === 'validation') {
            section.rightValidationMode = 'none';
          }
        }

        section.loaded = true;

        this.applyDiffModels(key, section.leftContent, section.rightContent);
        this.scheduleEditorMount(key);
        this.cdr.markForCheck();
      },
      error: () => {
        section.loading = false;
        section.error = 'Unable to load comparison data.';
        this.cdr.markForCheck();
      },
    });
  }

  private loadLookupsSection(section: CompareSectionState): void {
    const leftBody = { ContextId: this.leftInterface.UniqueID, Handler: '' };
    const rightBody = { ContextId: this.rightInterface.UniqueID, Handler: '' };

    Promise.all([
      this.service.postapi('api/XMLMappings/GetByHandler', leftBody).catch(() => null),
      this.service.postapi('api/XMLMappings/GetByHandler', rightBody).catch(() => null),
    ])
      .then(([leftData, rightData]) => {
        section.leftLookups = this.normalizeLookups(leftData);
        section.rightLookups = this.normalizeLookups(rightData);
        section.loaded = true;
        section.loading = false;
        this.cdr.markForCheck();
      })
      .catch(() => {
        section.error = 'Unable to load lookups.';
        section.loading = false;
        this.cdr.markForCheck();
      });
  }

  private scheduleEditorMount(key: CompareEditorKey): void {
    const section = this.sections[key];
    section.showEditor = false;
    this.clearDiffEditor(key);
    setTimeout(() => {
      if (section.expanded && section.loaded) {
        section.showEditor = true;
        this.cdr.markForCheck();
      }
    }, 0);
  }

  private setSectionEditing(key: CompareEditorKey, editing: boolean): void {
    const options =
      key === 'transform'
        ? this.transformDiffOptions
        : key === 'validation'
          ? this.validationDiffOptions
          : key === 'delivery'
            ? this.deliveryDiffOptions
            : key === 'rule'
              ? this.ruleDiffOptions
              : this.vaRulesDiffOptions;
    const leftEditable = editing && this.isSideEditable('left');
    const rightEditable = editing && this.isSideEditable('right');
    options['readOnly'] = !leftEditable && !rightEditable;
    options['originalEditable'] = leftEditable;
    options['modifiedEditable'] = rightEditable;
  }

  private fetchTransformMapping$(iface: any): Observable<MappingLoadResult> {
    const url =
      'api/GetMappings/Get?routeid=' +
      iface.id +
      '&processorid=' +
      iface.PAID +
      '&processorapikey=' +
      iface.PAAPIKEY;
    return this.service.getapiurl(url).pipe(
      map((response) => this.buildMappingLoadResult(response as any, false)),
    );
  }

  private getMappingFetcher(
    key: 'transform' | 'validation' | 'delivery' | 'rule' | 'vaRules',
  ): (iface: any) => Observable<MappingLoadResult | string> {
    switch (key) {
      case 'transform':
        return (iface) => this.fetchTransformMapping$(iface);
      case 'validation':
        return (iface) => this.fetchValidationMapping$(iface);
      case 'delivery':
        return (iface) => this.fetchRuleConfigByStage$(iface, 'Delivered');
      case 'rule':
        return (iface) => this.fetchRuleConfigByStage$(iface, 'Validation');
      case 'vaRules':
        return (iface) => this.fetchVaRules$(iface);
    }
  }

  private emptyMappingMeta(): CompareMappingSaveMeta {
    return {
      template: '0',
      resultjson: '',
      version: '1',
      coreConfigVersionList: [],
      liveVersion: '',
    };
  }

  private getLiveCoreConfigFromMappingsResponse(response: any): any {
    const list = response?.CoreConfigVersionList;
    if (!Array.isArray(list)) {
      return null;
    }
    return list.find((c: any) => c?.islive === true) ?? null;
  }

  /** Prefer live; otherwise latest — supports inactive interfaces with no live version. */
  private getPreferredCoreConfigFromMappingsResponse(response: any): any {
    const list = this.normalizeCoreVersionList(response?.CoreConfigVersionList);
    return this.pickDefaultCoreVersionRow(list);
  }

  /**
   * Strict check: preferred version's processorconfig.validationconfig only (not transform mappedxml).
   * Same rule as CIIS / Create Interface toggle and modal routing.
   */
  private hasLegacyValidationConfig(response: any): boolean {
    const preferred = this.getPreferredCoreConfigFromMappingsResponse(response);
    const vc = preferred?.json?.processorconfig?.validationconfig;
    if (!vc) {
      return false;
    }
    const mappedxml = (vc.mappedxml ?? '').toString().trim();
    const generatedxsl = (vc.generatedxsl ?? '').toString().trim();
    return !!(mappedxml || generatedxsl);
  }

  private buildMappingLoadResult(data: any, isValidation: boolean): MappingLoadResult {
    const versionList = this.normalizeCoreVersionList(data?.CoreConfigVersionList);
    const selectedRow = this.pickDefaultCoreVersionRow(versionList);
    const selectedVersion = selectedRow ? String(selectedRow.version) : '';

    let rawXml: any = selectedRow
      ? this.extractMappedXmlFromCoreVersionRow(selectedRow, isValidation)
      : null;

    if ((rawXml == null || String(rawXml).trim() === '') && !isValidation) {
      // Fallback: GetMappings top-level mappedxml (typically live/current).
      rawXml = data?.mappedxml;
    }

    if (isValidation) {
      if (rawXml == null || String(rawXml).trim() === '') {
        throw new Error('No legacy validation mapping (validationconfig) found');
      }
    } else if (rawXml == null) {
      this.toastr.warning("Template configurations not found", "", {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });
    }

    const raw = String(rawXml);
    return {
      displayXml: this.formatXml(raw),
      rawXml: raw,
      meta: this.extractMappingSaveMeta(data, isValidation),
      versionList,
      selectedVersion,
      versionSource: 'core',
    };
  }

  private extractMappingSaveMeta(data: any, isValidation: boolean): CompareMappingSaveMeta {
    const coreConfigVersionList = Array.isArray(data?.CoreConfigVersionList)
      ? data.CoreConfigVersionList
      : [];
    const liveEntry = coreConfigVersionList.find((c: any) => c.islive === true);
    const liveVersion = liveEntry?.version != null ? String(liveEntry.version) : '';
    let resultjson = '';
    if (data?.resultjson != null && data.resultjson !== '') {
      resultjson =
        typeof data.resultjson === 'string' ? data.resultjson : JSON.stringify(data.resultjson);
    }
    return {
      template: data?.template != null ? String(data.template) : '0',
      resultjson,
      version: '',
      coreConfigVersionList,
      liveVersion,
    };
  }

  /**
   * Mirrors xmlmappings savemappeddataDB (1356–1369). Compare has no Push to QC / Approve —
   * save always ends in Dev (100); API resets QC/Live to Dev on update, or creates next version when live.
   */
  private resolveVersionForSave(meta: CompareMappingSaveMeta | null, isValidation: boolean): string {
    if (!meta?.coreConfigVersionList?.length) {
      return meta?.version || '1';
    }
    const versionDetails = meta.coreConfigVersionList;
    const lastVersion = String(versionDetails[versionDetails.length - 1]?.version ?? '');
    const liveVersion = meta.liveVersion?.trim() ?? '';

    if (isValidation) {
      return liveVersion || lastVersion || '1';
    }

    if (liveVersion) {
      if (parseInt(lastVersion, 10) > parseInt(liveVersion, 10)) {
        return lastVersion;
      }
      return '';
    }

    return lastVersion || '1';
  }

  private applyDiffModels(
    key: CompareEditorKey,
    left: string,
    right: string,
  ): void {
    if (key === 'transform') {
      this.transformOldModel = { code: left, language: 'xml' };
      this.transformNewModel = { code: right, language: 'xml' };
    } else if (key === 'validation') {
      const validationSection = this.sections.validation;
      this.validationOldModel = {
        code: left,
        language: validationSection.leftValidationMode === 'rules' ? 'json' : 'xml',
      };
      this.validationNewModel = {
        code: right,
        language: validationSection.rightValidationMode === 'rules' ? 'json' : 'xml',
      };
    } else if (key === 'delivery') {
      this.deliveryOldModel = { code: left, language: 'json' };
      this.deliveryNewModel = { code: right, language: 'json' };
    } else if (key === 'rule') {
      this.ruleOldModel = { code: left, language: 'json' };
      this.ruleNewModel = { code: right, language: 'json' };
    } else {
      this.vaRulesOldModel = { code: left, language: 'json' };
      this.vaRulesNewModel = { code: right, language: 'json' };
    }
  }

  startEditSection(key: CompareEditorKey): void {
    const section = this.sections[key];
    if (!this.canEditSection(key) || section.loading || section.saving) {
      return;
    }
    section.editing = true;
    section.leftBackup = section.leftContent;
    section.rightBackup = section.rightContent;
    section.showEditor = false;
    this.setSectionEditing(key, true);
    this.applyDiffModels(key, section.leftContent, section.rightContent);
    setTimeout(() => {
      if (section.editing) {
        section.showEditor = true;
        this.cdr.markForCheck();
      }
    }, 0);
    this.cdr.markForCheck();
  }

  saveSection(key: CompareEditorKey): void {
    const section = this.sections[key];
    if (!section.editing || section.saving) {
      return;
    }
    const panes = this.getBothEditorContent(key);
    const leftChanged = this.contentChanged(panes.left, section.leftBackup);
    const rightChanged = this.contentChanged(panes.right, section.rightBackup);

    if (!leftChanged && !rightChanged) {
      this.toastr.info('No changes to save.', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    if (key === 'transform') {
      this.saveXmlSection(key, panes, leftChanged, rightChanged);
      return;
    }
    if (key === 'validation') {
      this.saveValidationSection(panes, leftChanged, rightChanged);
      return;
    }
    if (key === 'delivery' || key === 'rule') {
      const stage = key === 'delivery' ? 'Delivered' : 'Validation';
      this.saveRuleSection(key, panes, leftChanged, rightChanged, stage);
    }
  }

  private saveXmlSection(
    key: 'transform',
    panes: { left: string; right: string },
    leftChanged: boolean,
    rightChanged: boolean,
  ): void {
    const section = this.sections[key];
    const saves: Observable<
      { ok: true; result: XmlMappingSaveResult } | { ok: false; side: ComparePaneSide; message: string }
    >[] = [];

    if (leftChanged) {
      if (!this.isSideEditable('left')) {
        this.toastr.warning('Left interface is locked. Left changes were not saved.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      } else {
        saves.push(
          this.asSideSaveOutcome$(
            this.saveXmlForSide(
              'left',
              panes.left,
              this.withSelectedProcessor(this.leftInterface, 'left'),
              section.leftSaveMeta,
              false,
              key,
            ),
            'left',
            'Unable to save mapping.',
          ),
        );
      }
    }

    if (rightChanged) {
      if (!this.isSideEditable('right')) {
        this.toastr.warning('Right interface is locked. Right changes were not saved.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      } else {
        saves.push(
          this.asSideSaveOutcome$(
            this.saveXmlForSide(
              'right',
              panes.right,
              this.withSelectedProcessor(this.rightInterface, 'right'),
              section.rightSaveMeta,
              false,
              key,
            ),
            'right',
            'Unable to save mapping.',
          ),
        );
      }
    }

    if (!saves.length) {
      return;
    }

    section.saving = true;
    this.cdr.markForCheck();

    forkJoin(saves).subscribe({
      next: (outcomes) => {
        section.saving = false;
        const successes = outcomes.filter(
          (o): o is { ok: true; result: XmlMappingSaveResult } => o.ok,
        );
        const failures = outcomes.filter(
          (o): o is { ok: false; side: ComparePaneSide; message: string } => !o.ok,
        );

        successes.forEach((o) => {
          this.applySavedPane(section, key, o.result.side, panes[o.result.side]);
          this.applySavedMeta(section, o.result.side, o.result.meta);
        });
        failures.forEach((f) => {
          if (f.side === 'left') {
            section.leftContent = panes.left;
          } else {
            section.rightContent = panes.right;
          }
        });

        if (failures.length === 0) {
          section.editing = false;
          this.setSectionEditing(key, false);
        }
        this.applyDiffModels(key, section.leftContent, section.rightContent);
        if (failures.length > 0) {
          this.scheduleEditorMount(key);
        }
        this.showSaveSuccessToasts(
          successes.map((o) => o.result),
          key,
        );
        this.showSaveFailureToasts(failures, key);
        this.cdr.markForCheck();
      },
      error: (err) => {
        section.saving = false;
        this.cdr.markForCheck();
        this.service.copyableToastError(err?.message || 'Unable to save mapping.');
      },
    });
  }

  /** Validation compare may mix legacy XML (core config) and pre-validation rules (RouteRuleMapping). */
  private saveValidationSection(
    panes: { left: string; right: string },
    leftChanged: boolean,
    rightChanged: boolean,
  ): void {
    const section = this.sections.validation;

    const saves: Observable<
      | { ok: true; result: ValidationSaveResult }
      | { ok: false; side: ComparePaneSide; message: string }
    >[] = [];

    if (leftChanged) {
      saves.push(
        ...this.buildValidationPaneSaves('left', panes.left, section.leftValidationMode).map(
          (save$) =>
            this.asSideSaveOutcome$(save$, 'left', 'Unable to save validation mapping.'),
        ),
      );
    }
    if (rightChanged) {
      saves.push(
        ...this.buildValidationPaneSaves('right', panes.right, section.rightValidationMode).map(
          (save$) =>
            this.asSideSaveOutcome$(save$, 'right', 'Unable to save validation mapping.'),
        ),
      );
    }

    if (!saves.length) {
      return;
    }

    section.saving = true;
    this.cdr.markForCheck();

    forkJoin(saves).subscribe({
      next: (outcomes) => {
        section.saving = false;
        const successes = outcomes.filter(
          (o): o is { ok: true; result: ValidationSaveResult } => o.ok,
        );
        const failures = outcomes.filter(
          (o): o is { ok: false; side: ComparePaneSide; message: string } => !o.ok,
        );

        successes.forEach((o) => {
          const result = o.result;
          const content =
            'refreshedContent' in result && result.refreshedContent != null
              ? result.refreshedContent
              : panes[result.side];
          this.applySavedPane(section, 'validation', result.side, content);
          if ('meta' in result && result.meta) {
            this.applySavedMeta(section, result.side, result.meta);
          }
        });
        failures.forEach((f) => {
          if (f.side === 'left') {
            section.leftContent = panes.left;
          } else {
            section.rightContent = panes.right;
          }
        });

        if (failures.length === 0) {
          section.editing = false;
          this.setSectionEditing('validation', false);
        }
        this.applyDiffModels('validation', section.leftContent, section.rightContent);
        if (failures.length > 0) {
          this.scheduleEditorMount('validation');
        }
        this.showSaveSuccessToasts(
          successes.map((o) => o.result),
          'validation',
        );
        this.showSaveFailureToasts(failures, 'validation');
        this.cdr.markForCheck();
      },
      error: (err) => {
        section.saving = false;
        this.cdr.markForCheck();
        if ((err?.message || '') === 'Invalid characters in rulename.') {
          return;
        }
        this.service.copyableToastError(err?.message || 'Unable to save validation mapping.');
      },
    });
  }

  private buildValidationPaneSaves(
    side: ComparePaneSide,
    content: string,
    mode: ValidationMappingMode,
  ): Observable<ValidationSaveResult>[] {
    if (!this.isSideEditable(side)) {
      this.toastr.warning(
        `${side === 'left' ? 'Left' : 'Right'} interface is locked. Changes were not saved.`,
        '',
        { timeOut: 4000, positionClass: 'toast-bottom-right' },
      );
      return [];
    }

    const formatError = this.validateValidationPaneContent(side, content, mode);
    if (formatError) {
      return [throwError(() => new Error(formatError))];
    }

    const section = this.sections.validation;
    const iface = this.withSelectedProcessor(
      side === 'left' ? this.leftInterface : this.rightInterface,
      side,
    );
    const meta = side === 'left' ? section.leftSaveMeta : section.rightSaveMeta;

    const effectiveMode = this.resolveValidationSaveMode(mode, content);

    if (effectiveMode === 'rules') {
      return [
        this.saveRuleForSide(side, content, iface, 'PreValidation', 'validation').pipe(
          switchMap((saveResult) =>
            this.fetchRuleConfigByStage$(iface, 'PreValidation').pipe(
              map((loaded) => {
                this.applyVersionStateFromLoad(section, side, loaded);
                if (side === 'left') {
                  section.leftValidationMode = 'rules';
                } else {
                  section.rightValidationMode = 'rules';
                }
                return {
                  side,
                  message: saveResult.message,
                  refreshedContent: loaded.displayXml,
                };
              }),
            ),
          ),
        ),
      ];
    }

    if (effectiveMode === 'legacy') {
      return [
        this.saveXmlForSide(side, content, iface, meta, true, 'validation').pipe(
          map((result) => {
            if (side === 'left') {
              section.leftValidationMode = 'legacy';
            } else {
              section.rightValidationMode = 'legacy';
            }
            return result;
          }),
        ),
      ];
    }

    return [
      throwError(
        () =>
          new Error(
            `${side === 'left' ? 'Left' : 'Right'} interface has no pre-validation rules or legacy validation mapping to save.`,
          ),
      ),
    ];
  }

  /**
   * When a side loaded as 'none', allow create-on-save from pasted content
   * (same Dev create path as Pre-Delivery when no versions exist).
   */
  private resolveValidationSaveMode(
    mode: ValidationMappingMode,
    content: string,
  ): ValidationMappingMode {
    if (mode === 'rules' || mode === 'legacy') {
      return mode;
    }
    const trimmed = (content ?? '').trim();
    if (!trimmed) {
      return 'none';
    }
    if (this.canParseJson(trimmed) && !this.looksLikeXml(trimmed)) {
      return 'rules';
    }
    if (this.looksLikeXml(trimmed)) {
      return 'legacy';
    }
    return 'none';
  }

  private refreshMappingSaveMeta$(
    iface: any,
    isValidation: boolean,
    fallbackMeta: CompareMappingSaveMeta | null,
  ): Observable<CompareMappingSaveMeta> {
    const fetch$ = isValidation
      ? this.fetchLegacyValidationMapping$(iface)
      : this.fetchTransformMapping$(iface);
    return fetch$.pipe(
      map((result) => result.meta),
      catchError(() => of(fallbackMeta ?? this.emptyMappingMeta())),
    );
  }

  private saveXmlForSide(
    side: ComparePaneSide,
    xml: string,
    iface: any,
    meta: CompareMappingSaveMeta | null,
    isValidation: boolean,
    sectionKey: 'transform' | 'validation',
  ): Observable<XmlMappingSaveResult> {
    const xmlToSave = (xml || '').trim();
    if (!xmlToSave) {
      return throwError(() => new Error(`${side === 'left' ? 'Left' : 'Right'} pane is empty.`));
    }
    const versionToUpdate = this.resolveVersionForSave(meta, isValidation);
    const templateId =
      meta?.template || iface?.TemplateID || iface?.templateid || '0';
    const apiUrl = isValidation
      ? 'api/CoreConfig/SaveValidationConfig'
      : 'api/CoreConfig/SaveTransformConfig';

    return this.service.XmlModification({ xml: xmlToSave, operation: 'delete' }).pipe(
      switchMap((convertResp: any) => {
        if (convertResp?.statusmessage != null) {
          return throwError(() => new Error(convertResp.statusmessage));
        }
        const payload = {
          mappedxml: convertResp?.responsebody ?? xmlToSave,
          routeid: iface?.id,
          routeuniqueid: Number(iface?.UniqueID || 0),
          processorid: iface?.PAID,
          processorapikey: iface?.PAAPIKEY,
          createdby: this.getCurrentUserId(),
          isupdateconfig: true,
          template: templateId,
          resultjson: meta?.resultjson ?? '',
          version: versionToUpdate,
          templatexml: '',
        };
        return this.service.savemappings(payload, apiUrl).pipe(
          switchMap((response) => {
            const parsed = this.parseSaveMappingsResponse(response);
            if (parsed.isFailure) {
              return throwError(() => new Error(parsed.message));
            }
            const mappingAudit = this.buildMappingSaveAudit(meta, versionToUpdate, sectionKey, side);
            this.recordPipelineSaveAudit(
              iface?.UniqueID,
              mappingAudit.actionType,
              mappingAudit.newValue,
              mappingAudit.description,
            );
            return this.refreshMappingSaveMeta$(iface, isValidation, meta).pipe(
              map((refreshedMeta) => ({
                side,
                message: parsed.message,
                meta: refreshedMeta,
              })),
            );
          }),
        );
      }),
    );
  }

  private parseSaveMappingsResponse(response: any): { isFailure: boolean; message: string } {
    try {
      const parsed = typeof response === 'string' ? JSON.parse(response) : response;
      const status = String(parsed?.status ?? '').toLowerCase();
      const message = parsed?.statusmessage || parsed?.StatusMessage || '';
      if (status === 'failure') {
        return { isFailure: true, message: message || 'Save failed.' };
      }
      return { isFailure: false, message: message || 'Saved successfully.' };
    } catch {
      return { isFailure: false, message: 'Saved successfully.' };
    }
  }

  private saveRuleSection(
    key: 'delivery' | 'rule',
    panes: { left: string; right: string },
    leftChanged: boolean,
    rightChanged: boolean,
    stage: RuleExecutionStage,
  ): void {
    const section = this.sections[key];

    const saves: Observable<
      | { ok: true; result: { side: ComparePaneSide; message: string } }
      | { ok: false; side: ComparePaneSide; message: string }
    >[] = [];

    if (leftChanged) {
      if (!this.isSideEditable('left')) {
        this.toastr.warning('Left interface is locked. Left changes were not saved.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      } else {
        saves.push(
          this.asSideSaveOutcome$(
            this.saveRuleForSide('left', panes.left, this.leftInterface, stage, key),
            'left',
            'Unable to save rules.',
          ),
        );
      }
    }

    if (rightChanged) {
      if (!this.isSideEditable('right')) {
        this.toastr.warning('Right interface is locked. Right changes were not saved.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      } else {
        saves.push(
          this.asSideSaveOutcome$(
            this.saveRuleForSide('right', panes.right, this.rightInterface, stage, key),
            'right',
            'Unable to save rules.',
          ),
        );
      }
    }

    if (!saves.length) {
      return;
    }

    section.saving = true;
    this.cdr.markForCheck();

    forkJoin(saves).subscribe({
      next: (outcomes) => {
        section.saving = false;
        const successes = outcomes.filter(
          (o): o is { ok: true; result: { side: ComparePaneSide; message: string } } => o.ok,
        );
        const failures = outcomes.filter(
          (o): o is { ok: false; side: ComparePaneSide; message: string } => !o.ok,
        );

        successes.forEach((o) =>
          this.applySavedPane(section, key, o.result.side, panes[o.result.side]),
        );
        failures.forEach((f) => {
          if (f.side === 'left') {
            section.leftContent = panes.left;
          } else {
            section.rightContent = panes.right;
          }
        });

        if (failures.length === 0) {
          section.editing = false;
          this.setSectionEditing(key, false);
        }
        this.applyDiffModels(key, section.leftContent, section.rightContent);
        if (failures.length > 0) {
          this.scheduleEditorMount(key);
        }
        this.showSaveSuccessToasts(
          successes.map((o) => o.result),
          key,
        );
        this.showSaveFailureToasts(failures, key);
        if (successes.length > 0 && (key === 'delivery' || key === 'rule')) {
          this.refreshRuleVersionListsAfterSave(
            key,
            stage,
            successes.map((o) => o.result.side),
          );
        } else {
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        section.saving = false;
        this.cdr.markForCheck();
        if ((err?.message || '') === 'Invalid characters in rulename.') {
          return;
        }
        this.service.copyableToastError(err?.message || 'Unable to save rules.');
      },
    });
  }

  private refreshRuleVersionListsAfterSave(
    key: 'delivery' | 'validation' | 'rule',
    stage: RuleExecutionStage,
    sides: ComparePaneSide[],
  ): void {
    const section = this.sections[key];
    const refreshes = sides.map((side) => {
      const iface = side === 'left' ? this.leftInterface : this.rightInterface;
      return this.fetchRuleConfigByStage$(iface, stage).pipe(
        map((loaded) => {
          this.applyVersionStateFromLoad(section, side, loaded);
          return side;
        }),
        catchError(() => of(side)),
      );
    });
    if (!refreshes.length) {
      this.cdr.markForCheck();
      return;
    }
    forkJoin(refreshes).subscribe({
      next: () => this.cdr.markForCheck(),
      error: () => this.cdr.markForCheck(),
    });
  }

  /**
   * Mirrors rule-config-component onClickSave (874–901).
   * Pre-Delivery: QC saves as Dev on the same version (compare has no Push to QC).
   */
  private resolveRuleVersionForSave(
    versions: any[],
    options?: { promoteQcToDev?: boolean },
  ): {
    targetVersion: number;
    targetStatus: string;
    isSameStatusUpdate: boolean;
    isNewRuleConfigVersion: boolean;
  } {
    const promoteQcToDev = options?.promoteQcToDev === true;
    const hasVersions = versions.length > 0;
    let targetVersion = 1;
    let targetStatus = 'Dev';
    let isSameStatusUpdate = false;
    let previousLatestNumericVersion = 0;

    if (hasVersions) {
      const latest = versions[versions.length - 1];
      const currentVersion = parseInt(String(latest?.Version ?? latest?.version ?? ''), 10) || 0;
      previousLatestNumericVersion = currentVersion || 0;
      const status = String(latest?.VersionStatus ?? latest?.versionStatus ?? 'Dev')
        .trim()
        .toLowerCase();

      if (status === 'dev' || status === 'qc') {
        targetVersion = currentVersion || 1;
        if (promoteQcToDev && status === 'qc') {
          targetStatus = 'Dev';
        } else {
          targetStatus =
            String(latest?.VersionStatus ?? latest?.versionStatus ?? 'Dev').trim() || 'Dev';
        }
        isSameStatusUpdate = true;
      } else if (status === 'live') {
        targetVersion = (currentVersion || 0) + 1;
        targetStatus = 'Dev';
      } else {
        targetVersion = currentVersion || 1;
        targetStatus = 'Dev';
      }
    }

    const isNewRuleConfigVersion =
      !isSameStatusUpdate &&
      (!hasVersions || targetVersion > previousLatestNumericVersion);

    return {
      targetVersion,
      targetStatus,
      isSameStatusUpdate,
      isNewRuleConfigVersion,
    };
  }

  private saveRuleForSide(
    side: ComparePaneSide,
    jsonText: string,
    iface: any,
    stage: RuleExecutionStage,
    sectionKey: CompareEditorKey,
  ): Observable<{ side: ComparePaneSide; message: string }> {
    let parsed: any;
    try {
      parsed = JSON.parse((jsonText || '[]').trim() || '[]');
    } catch {
      return throwError(
        () => new Error(`${side === 'left' ? 'Left' : 'Right'} rule JSON is invalid.`),
      );
    }

    if (this.hasInvalidRuleNames(parsed)) {
      return throwError(() => new Error('Invalid characters in rulename.'));
    }

    return this.ruleEngineService.GetRuleConfigVersions(iface?.UniqueID, stage).pipe(
      switchMap((versionsRaw) => {
        const versions = Array.isArray(versionsRaw) ? versionsRaw : [];
        const resolved = this.resolveRuleVersionForSave(versions, {
          promoteQcToDev: sectionKey === 'delivery',
        });
        const { targetVersion, targetStatus, isNewRuleConfigVersion } = resolved;

        const body = {
          RouteUniqueId: iface?.UniqueID,
          RouteGuid: iface?.id,
          RuleConfig: JSON.stringify(parsed),
          RuleExecutionStage: stage,
          Version: targetVersion,
          VersionStatus: targetStatus,
          IsLive: false,
        };
        return this.ruleEngineService.InsertAndUpdateRuleConfig(body).pipe(
          map(() => {
            const ruleAudit = this.buildRuleSaveAudit(
              stage,
              sectionKey,
              side,
              targetVersion,
              targetStatus,
              isNewRuleConfigVersion,
            );
            this.recordPipelineSaveAudit(
              iface?.UniqueID,
              ruleAudit.actionType,
              ruleAudit.newValue,
              ruleAudit.description,
            );
            return { side, message: 'Saved successfully.' };
          }),
        );
      }),
    );
  }

  private hasInvalidRuleNames(parsed: any): boolean {
    const names = this.collectRuleNames(parsed);
    return names.some((name) => !this.isAllowedCompareRuleName(name));
  }

  /** Allowed: a-z, A-Z, 0-9, underscore only. */
  private isAllowedCompareRuleName(ruleName: string): boolean {
    const name = (ruleName ?? '').toString().trim();
    if (!name) {
      return true;
    }
    return /^[a-zA-Z0-9_ ]+$/.test(name);
  }

  private collectRuleNames(node: any, found: string[] = []): string[] {
    if (node == null) {
      return found;
    }
    if (Array.isArray(node)) {
      node.forEach((item) => this.collectRuleNames(item, found));
      return found;
    }
    if (typeof node === 'object') {
      const name = node.rulename ?? node.RuleName;
      if (name != null && String(name).trim() !== '') {
        found.push(String(name).trim());
      }
      Object.keys(node).forEach((key) => {
        if (key === 'rulename' || key === 'RuleName') {
          return;
        }
        this.collectRuleNames(node[key], found);
      });
    }
    return found;
  }

  private applySavedPane(
    section: CompareSectionState,
    key: CompareEditorKey,
    side: ComparePaneSide,
    content: string,
  ): void {
    const normalized = content ?? '';
    if (side === 'left') {
      section.leftContent = normalized;
      section.leftBackup = normalized;
    } else {
      section.rightContent = normalized;
      section.rightBackup = normalized;
    }
  }

  private applySavedMeta(
    section: CompareSectionState,
    side: ComparePaneSide,
    meta: CompareMappingSaveMeta,
  ): void {
    if (side === 'left') {
      section.leftSaveMeta = meta;
    } else {
      section.rightSaveMeta = meta;
    }
    if (meta?.coreConfigVersionList?.length) {
      const list = this.normalizeCoreVersionList(meta.coreConfigVersionList);
      const picked = this.pickDefaultCoreVersionRow(list);
      if (side === 'left') {
        section.leftVersionList = list;
        section.leftVersionValue = picked ? String(picked.version) : '';
        section.leftVersionSource = 'core';
      } else {
        section.rightVersionList = list;
        section.rightVersionValue = picked ? String(picked.version) : '';
        section.rightVersionSource = 'core';
      }
    }
  }

  private applyVersionStateFromLoad(
    section: CompareSectionState,
    side: ComparePaneSide,
    result: MappingLoadResult,
  ): void {
    const list = Array.isArray(result.versionList) ? result.versionList : [];
    const source = result.versionSource ?? (list.length ? 'core' : 'none');
    const selected = result.selectedVersion != null ? String(result.selectedVersion) : '';
    if (side === 'left') {
      section.leftVersionList = list;
      section.leftVersionValue = selected;
      section.leftVersionSource = source;
    } else {
      section.rightVersionList = list;
      section.rightVersionValue = selected;
      section.rightVersionSource = source;
    }
  }

  private clearVersionState(section: CompareSectionState, side: ComparePaneSide): void {
    if (side === 'left') {
      section.leftVersionList = [];
      section.leftVersionValue = '';
      section.leftVersionSource = 'none';
    } else {
      section.rightVersionList = [];
      section.rightVersionValue = '';
      section.rightVersionSource = 'none';
    }
  }

  /**
   * Left/right version change — mirrors compare.component ChangeVersion(parent|child).
   * Reloads that pane only; does not affect save target (still Dev).
   */
  onCompareVersionChange(
    key: 'transform' | 'validation' | 'delivery' | 'rule',
    side: ComparePaneSide,
    event: { value?: string } | string,
  ): void {
    const section = this.sections[key];
    if (section.editing || section.saving || section.loading) {
      return;
    }
    const raw = typeof event === 'string' ? event : event?.value != null ? String(event.value) : '';
    const version = String(raw).replace(/^\s*Live\s*/i, '').trim();
    if (!version) {
      return;
    }
    if (side === 'left') {
      section.leftVersionValue = version;
    } else {
      section.rightVersionValue = version;
    }

    const source = side === 'left' ? section.leftVersionSource : section.rightVersionSource;
    const iface = this.withSelectedProcessor(
      side === 'left' ? this.leftInterface : this.rightInterface,
      side,
    );
    section.loading = true;
    this.cdr.markForCheck();

    const load$ =
      source === 'rules'
        ? this.loadRuleVersionContent$(section, side, key, version)
        : this.loadCoreVersionContent$(iface, key, side, version);

    load$.subscribe({
      next: (content) => {
        section.loading = false;
        if (side === 'left') {
          section.leftContent = content.displayXml;
          section.leftContentRaw = content.rawXml;
          section.leftBackup = content.displayXml;
          if (content.meta) {
            section.leftSaveMeta = content.meta;
          }
          if (key === 'validation' && content.validationMode) {
            section.leftValidationMode = content.validationMode;
          }
        } else {
          section.rightContent = content.displayXml;
          section.rightContentRaw = content.rawXml;
          section.rightBackup = content.displayXml;
          if (content.meta) {
            section.rightSaveMeta = content.meta;
          }
          if (key === 'validation' && content.validationMode) {
            section.rightValidationMode = content.validationMode;
          }
        }
        this.applyDiffModels(key, section.leftContent, section.rightContent);
        this.scheduleEditorMount(key);
        this.cdr.markForCheck();
      },
      error: (err) => {
        section.loading = false;
        this.toastr.error(this.errorMessage(err) || 'Failed to load mapping version', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Change processor for a side (multi-processor routes). Reloads Transform / Validation
   * panes that are already loaded; save still uses Dev flow for the selected processor.
   */
  onCompareProcessorChange(side: ComparePaneSide, event: { value?: string } | string): void {
    const raw = typeof event === 'string' ? event : event?.value != null ? String(event.value) : '';
    const processorId = String(raw).trim();
    if (!processorId) {
      return;
    }

    // [(ngModel)] may already have updated selected id before selectionChange.
    // Compare against last *loaded* processor, not the ngModel value.
    const loadedId = side === 'left' ? this.leftLoadedProcessorId : this.rightLoadedProcessorId;
    if (loadedId === processorId) {
      return;
    }

    if (side === 'left') {
      this.leftSelectedProcessorId = processorId;
      this.leftLoadedProcessorId = processorId;
    } else {
      this.rightSelectedProcessorId = processorId;
      this.rightLoadedProcessorId = processorId;
    }

    (['transform', 'validation'] as const).forEach((key) => {
      const section = this.sections[key];
      if (!section.loaded) {
        return;
      }
      if (section.editing) {
        this.cancelSectionEdit(key);
      }
      this.reloadMappingPane(key, side);
    });
    this.cdr.markForCheck();
  }

  private reloadMappingPane(key: 'transform' | 'validation', side: ComparePaneSide): void {
    const section = this.sections[key];
    const iface = this.withSelectedProcessor(
      side === 'left' ? this.leftInterface : this.rightInterface,
      side,
    );
    const fetch$ = this.getMappingFetcher(key);
    section.loading = true;
    this.cdr.markForCheck();

    fetch$(iface)
      .pipe(catchError((err) => of({ error: this.errorMessage(err) })))
      .subscribe({
        next: (result) => {
          section.loading = false;
          if (this.isMappingLoadError(result)) {
            if (side === 'left') {
              section.leftContent = '';
              section.leftContentRaw = '';
              section.leftBackup = '';
              section.leftSaveMeta = null;
              this.clearVersionState(section, 'left');
              if (key === 'validation') {
                section.leftValidationMode = 'none';
              }
            } else {
              section.rightContent = '';
              section.rightContentRaw = '';
              section.rightBackup = '';
              section.rightSaveMeta = null;
              this.clearVersionState(section, 'right');
              if (key === 'validation') {
                section.rightValidationMode = 'none';
              }
            }
            section.error = result.error;
          } else if (this.isMappingLoadResult(result)) {
            if (side === 'left') {
              section.leftContent = result.displayXml;
              section.leftContentRaw = result.rawXml;
              section.leftBackup = result.displayXml;
              section.leftSaveMeta = result.meta;
              this.applyVersionStateFromLoad(section, 'left', result);
              if (key === 'validation') {
                section.leftValidationMode = result.validationMode ?? 'legacy';
              }
            } else {
              section.rightContent = result.displayXml;
              section.rightContentRaw = result.rawXml;
              section.rightBackup = result.displayXml;
              section.rightSaveMeta = result.meta;
              this.applyVersionStateFromLoad(section, 'right', result);
              if (key === 'validation') {
                section.rightValidationMode = result.validationMode ?? 'legacy';
              }
            }
            section.error = null;
          }
          this.applyDiffModels(key, section.leftContent, section.rightContent);
          this.scheduleEditorMount(key);
          this.cdr.markForCheck();
        },
        error: () => {
          section.loading = false;
          section.error = 'Unable to load comparison data.';
          this.cdr.markForCheck();
        },
      });
  }

  private loadCoreVersionContent$(
    iface: any,
    key: 'transform' | 'validation' | 'delivery' | 'rule',
    side: ComparePaneSide,
    version: string,
  ): Observable<{ displayXml: string; rawXml: string; meta?: CompareMappingSaveMeta; validationMode?: ValidationMappingMode }> {
    const section = this.sections[key];
    const list = side === 'left' ? section.leftVersionList : section.rightVersionList;
    const existing = list.find((v) => String(v.version) === String(version));
    const fromRow = existing
      ? this.extractMappedXmlFromCoreVersionRow(existing, key === 'validation')
      : null;

    if (fromRow != null && String(fromRow).trim() !== '') {
      const raw = String(fromRow);
      return of({
        displayXml: key === 'validation' || key === 'transform' ? this.formatXml(raw) : raw,
        rawXml: raw,
        validationMode: key === 'validation' ? ('legacy' as ValidationMappingMode) : undefined,
      });
    }

    const url =
      'api/CoreConfig/GetCoreconfigVersion?routeid=' +
      encodeURIComponent(iface.id) +
      '&agentid=' +
      encodeURIComponent(iface.PAID) +
      '&version=' +
      encodeURIComponent(version);

    return this.service.getapiurl(url).pipe(
      map((response) => {
        const rows = Array.isArray(response) ? response : [];
        if (!rows.length) {
          throw new Error('No mapping data for this version');
        }
        const ele = rows[0];
        const ix = list.findIndex((x) => String(x.version) === String(ele.version));
        if (ix >= 0) {
          list[ix] = { ...list[ix], ...ele };
        }
        const mapped = this.extractMappedXmlFromCoreVersionRow(ele, key === 'validation');
        if (mapped == null || String(mapped).trim() === '') {
          throw new Error('No data (processorconfig is empty) found for the selected version');
        }
        const raw = String(mapped);
        return {
          displayXml: this.formatXml(raw),
          rawXml: raw,
          validationMode: key === 'validation' ? ('legacy' as ValidationMappingMode) : undefined,
        };
      }),
    );
  }

  private loadRuleVersionContent$(
    section: CompareSectionState,
    side: ComparePaneSide,
    key: 'transform' | 'validation' | 'delivery' | 'rule',
    version: string,
  ): Observable<{
    displayXml: string;
    rawXml: string;
    meta?: CompareMappingSaveMeta;
    validationMode?: ValidationMappingMode;
  }> {
    const list = side === 'left' ? section.leftVersionList : section.rightVersionList;
    const row = list.find((v) => String(v.version) === String(version));
    if (!row) {
      return throwError(() => new Error('Selected version not found'));
    }
    const ruleData = this.extractRuleConfigFromVersionRow(row);
    const display = JSON.stringify(ruleData ?? [], null, 2);
    return of({
      displayXml: display,
      rawXml: display,
      validationMode: key === 'validation' ? ('rules' as ValidationMappingMode) : undefined,
    });
  }

  private extractMappedXmlFromCoreVersionRow(row: any, isValidation: boolean): string | null {
    if (!row) {
      return null;
    }
    const json = row.json;
    if (!json || typeof json !== 'object' || !Object.keys(json).length) {
      return null;
    }
    if (isValidation) {
      const mapped = json?.processorconfig?.validationconfig?.mappedxml;
      return mapped != null ? String(mapped) : null;
    }
    const mapped = json?.processorconfig?.xslconfig?.mappedxml;
    return mapped != null ? String(mapped) : null;
  }

  private normalizeCoreVersionList(list: any[]): any[] {
    return (Array.isArray(list) ? list : []).map((row) => ({
      ...row,
      version: row?.version != null ? String(row.version) : '',
    }));
  }

  private pickDefaultCoreVersionRow(list: any[]): any | null {
    if (!list?.length) {
      return null;
    }
    const live = list.find((c) => c?.islive === true);
    return live ?? list[list.length - 1] ?? null;
  }

  /** Normalize RuleConfig versions so the same Live/QC/Dev option template can be reused.
   * Older Live rows are marked Archived (same as rule-config-component.applyDisplayStatusToVersionOptions).
   */
  private normalizeRuleVersionList(list: any[]): any[] {
    const normalized = (Array.isArray(list) ? list : []).map((v, idx) => {
      const version = String(v?.Version ?? v?.version ?? idx + 1);
      const status = String(v?.VersionStatus ?? v?.versionStatus ?? '')
        .trim()
        .toLowerCase();
      const isLiveStatus = status === 'live';
      let versionstatus = '';
      if (status === 'dev') {
        versionstatus = '100';
      } else if (status === 'qc') {
        versionstatus = '101';
      } else if (status === 'live') {
        versionstatus = '103';
      }
      return {
        ...v,
        version,
        versionstatus,
        status,
        islive: isLiveStatus,
        isLatestLive: false,
        isArchivedLive: false,
        displayStatus: '',
        RuleConfig: v?.RuleConfig ?? v?.ruleConfig,
      };
    });

    let latestLiveIndex = -1;
    normalized.forEach((row, idx) => {
      if (row.status === 'live') {
        latestLiveIndex = idx;
      }
    });
    if (latestLiveIndex >= 0) {
      normalized[latestLiveIndex].isLatestLive = true;
    }

    normalized.forEach((row) => {
      const isArchivedLive = row.status === 'live' && !row.isLatestLive;
      row.isArchivedLive = isArchivedLive;
      row.islive = row.status === 'live' && row.isLatestLive;
      row.displayStatus = isArchivedLive ? 'Archived' : String(row.status ?? '');
      if (isArchivedLive) {
        // Avoid bare versionstatus=='103' span competing with Archived label.
        row.versionstatus = '';
      }
    });

    return normalized;
  }

  private pickDefaultRuleVersionRow(list: any[]): any | null {
    if (!list?.length) {
      return null;
    }
    const latestLive = list.find((c) => c?.isLatestLive === true) ?? list.filter((c) => c?.islive === true).pop();
    return latestLive ?? list[list.length - 1] ?? null;
  }

  private extractRuleConfigFromVersionRow(row: any): any[] {
    if (!row) {
      return [];
    }
    const configData = row?.RuleConfig ?? row?.ruleConfig;
    if (!configData) {
      return [];
    }
    const savedRuleConfig = this.parseRuleConfig(configData);
    if (!savedRuleConfig) {
      return [];
    }
    return Array.isArray(savedRuleConfig) ? savedRuleConfig : [savedRuleConfig];
  }

  private contentChanged(current: string, backup: string): boolean {
    return (current ?? '').trim() !== (backup ?? '').trim();
  }

  /** Read live left (original) and right (modified) panes from Monaco. */
  private getBothEditorContent(key: CompareEditorKey): { left: string; right: string } {
    const editor = this.diffEditors[key];
    const diffModel = editor?.getModel?.();
    const left =
      diffModel?.original?.getValue?.() ?? this.getFallbackModelCode(key, 'left');
    const right =
      diffModel?.modified?.getValue?.() ?? this.getFallbackModelCode(key, 'right');
    return { left, right };
  }

  private getFallbackModelCode(key: CompareEditorKey, side: ComparePaneSide): string {
    if (key === 'transform') {
      return side === 'left' ? this.transformOldModel.code : this.transformNewModel.code;
    }
    if (key === 'validation') {
      return side === 'left' ? this.validationOldModel.code : this.validationNewModel.code;
    }
    if (key === 'delivery') {
      return side === 'left' ? this.deliveryOldModel.code : this.deliveryNewModel.code;
    }
    if (key === 'rule') {
      return side === 'left' ? this.ruleOldModel.code : this.ruleNewModel.code;
    }
    return side === 'left' ? this.vaRulesOldModel.code : this.vaRulesNewModel.code;
  }

  private getCurrentUserId(): string {
    try {
      return JSON.parse(sessionStorage.getItem('sessionObjectlogin') || '{}')?.userdata?.userid || '';
    } catch {
      return '';
    }
  }

  /** Pipeline rules (Create Rule button) and delivery rules share the same version API. */
  private fetchRuleConfigByStage$(iface: any, ruleExecutionStage: string): Observable<MappingLoadResult> {
    return this.ruleEngineService.GetRuleConfigVersions(iface.UniqueID, ruleExecutionStage).pipe(
      map((raw) => {
        const normalized = this.normalizeRuleVersionList(Array.isArray(raw) ? raw : []);
        const selected = this.pickDefaultRuleVersionRow(normalized);
        const ruleData = selected ? this.extractRuleConfigFromVersionRow(selected) : [];
        const display = JSON.stringify(ruleData ?? [], null, 2);
        return {
          displayXml: display,
          rawXml: display,
          meta: this.emptyMappingMeta(),
          versionList: normalized,
          selectedVersion: selected ? String(selected.version) : '',
          versionSource: 'rules' as CompareVersionSource,
          validationMode: 'rules' as ValidationMappingMode,
        };
      }),
    );
  }

  /** Same extraction as InterfaceComponent.extractRuleConfigFromVersions / openDeliveryMapping. */
  private extractRuleConfigFromVersions(list: any[]): any[] {
    if (!list?.length) {
      return [];
    }
    const normalized = this.normalizeRuleVersionList(list);
    const selected = this.pickDefaultRuleVersionRow(normalized);
    return selected ? this.extractRuleConfigFromVersionRow(selected) : [];
  }

  private parseRuleConfig(configData: any): any {
    try {
      if (typeof configData === 'string') {
        configData = JSON.parse(configData);
      }
      if (configData && typeof configData === 'object' && configData['RuleConfig']) {
        const nestedConfig = configData['RuleConfig'];
        return typeof nestedConfig === 'string' ? JSON.parse(nestedConfig) : nestedConfig;
      }
      return configData;
    } catch {
      return null;
    }
  }

  /** VA rules for pipeline — same API as VarulesComponent.getvarules. */
  private fetchVaRules$(iface: any): Observable<MappingLoadResult> {
    const groupId = encodeURIComponent(this.resolveVaRuleGroupId(iface));
    const deploymentKey = this.resolveVaRulesDeploymentKey(iface);
    if (!groupId) {
      return throwError(() => new Error('Pipeline group id is missing.'));
    }
    if (!deploymentKey) {
      return throwError(() => new Error('Deployment key is missing for VA rules.'));
    }
    const apiname = `api/VitalHubDXAPI/getvarule?GroupId=${groupId}`;
    return this.w700Service.getapi(apiname, deploymentKey).pipe(
      map((data) => {
        const json = this.formatVaRulesJson(data);
        return {
          displayXml: json,
          rawXml: json,
          meta: this.emptyMappingMeta(),
        };
      }),
    );
  }

  private resolveVaRuleGroupId(iface: any): string {
    for (const key of ['id', 'Id', 'RouteGuid', 'groupid', 'GroupId']) {
      const val = iface?.[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val).trim();
      }
    }
    return '';
  }

  private resolveVaRuleName(iface: any): string {
    for (const key of ['displayname', 'DisplayName']) {
      const val = iface?.[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val).trim();
      }
    }
    return '';
  }

  /** Inbound → target deploy key; outbound → source (create / interface openvarulepopup). */
  private resolveVaRulesDeploymentKey(iface: any): string {
    const direction = String(iface?.Direction ?? iface?.direction ?? '').toLowerCase();
    if (direction.includes('inbound')) {
      return String(
        iface?.DEDEPLOYMENTKEY ??
          iface?.dedeploymentkey ??
          iface?.targetdeploy ??
          '',
      ).trim();
    }
    return String(
      iface?.SEDEPLOYMENTKEY ??
        iface?.sedeploymentkey ??
        iface?.sourcedeploy ??
        '',
    ).trim();
  }

  private formatVaRulesJson(data: any): string {
    const list = Array.isArray(data) ? data : [];
    try {
      return JSON.stringify(list, null, 2);
    } catch {
      return '[]';
    }
  }

  private fetchValidationMapping$(iface: any): Observable<MappingLoadResult> {
    const uniqueId = iface?.UniqueID;
    if (!uniqueId) {
      return this.fetchLegacyValidationMapping$(iface);
    }
    return this.ruleEngineService.GetRuleConfigVersions(uniqueId, 'PreValidation').pipe(
      switchMap((raw) => {
        const list = Array.isArray(raw) ? raw : [];
        if (list.length > 0) {
          return this.fetchRuleConfigByStage$(iface, 'PreValidation');
        }
        return this.fetchLegacyValidationMapping$(iface);
      }),
    );
  }

  /** Legacy validationconfig from GetMappings (prevalidation=true). Strict: no transform mappedxml. */
  private fetchLegacyValidationMapping$(iface: any): Observable<MappingLoadResult> {
    const url =
      'api/GetMappings/Get?routeid=' +
      iface.id +
      '&processorid=' +
      iface.PAID +
      '&processorapikey=' +
      iface.PAAPIKEY +
      '&prevalidation=true';
    return this.service.getapiurl(url).pipe(
      map((response) => {
        if (!this.hasLegacyValidationConfig(response)) {
          return this.emptyValidationMappingResult(iface);
        }
        const preferred = this.getPreferredCoreConfigFromMappingsResponse(response);
        const mappedXml = preferred?.json?.processorconfig?.validationconfig?.mappedxml;
        if (mappedXml == null || String(mappedXml).trim() === '') {
          // generatedxsl-only: treat as no displayable validation mapping for compare.
          return this.emptyValidationMappingResult(iface);
        }
        const result = this.buildMappingLoadResult(response as unknown as Record<string, unknown>, true);
        return { ...result, validationMode: 'legacy' as ValidationMappingMode };
      }),
    );
  }

  private emptyValidationMappingResult(iface: any): MappingLoadResult {
    const name = this.resolveVaRuleName(iface) || 'Interface';
    this.toastr.warning(
      `${name}: no pre-validation rules or legacy validation mapping found.`,
      '',
      { timeOut: 4000, positionClass: 'toast-bottom-right' },
    );
    return {
      displayXml: '',
      rawXml: '',
      meta: this.emptyMappingMeta(),
      validationMode: 'none',
      versionList: [],
      selectedVersion: '',
      versionSource: 'none',
    };
  }

  private buildValidationRulesLoadResult(json: string): MappingLoadResult {
    return {
      displayXml: json,
      rawXml: json,
      meta: this.emptyMappingMeta(),
      validationMode: 'rules',
      versionSource: 'rules',
    };
  }

  /** Light-red diff: highlight only when type+id+value is absent on the other side. */
  isLookupRowDifferent(row: CompareLookupRow, side: ComparePaneSide): boolean {
    const section = this.sections.lookups;
    const otherRows =
      side === 'left' ? section.rightLookups : section.leftLookups;
    return !this.findLookupByFullKey(
      otherRows,
      row.lookupType,
      row.lookupId,
      row.lookupValue,
    );
  }

  /** Selected rows first for display; underlying arrays unchanged for copy logic. */
  getDisplayLookups(side: ComparePaneSide): CompareLookupRow[] {
    const section = this.sections.lookups;
    const rows =
      side === 'left' ? section.leftLookups : section.rightLookups;
    const selected = rows.filter((r) => r.selected);
    const unselected = rows.filter((r) => !r.selected);
    return [...selected, ...unselected];
  }

  hasLookupSelection(): boolean {
    const section = this.sections.lookups;
    return (
      section.leftLookups.some((r) => r.selected) ||
      section.rightLookups.some((r) => r.selected)
    );
  }

  cancelLookupSelection(): void {
    this.clearLookupSelections(this.sections.lookups);
    this.lookupUpdatePreview = null;
    this.cdr.markForCheck();
  }

  isLookupCheckboxDisabled(_side: ComparePaneSide): boolean {
    const section = this.sections.lookups;
    return section.lookupSyncing || section.loading || !!this.lookupUpdatePreview;
  }

  onLookupRowSelectionChange(side: ComparePaneSide, row: CompareLookupRow, checked: boolean): void {
    if (this.isLookupCheckboxDisabled(side)) {
      return;
    }
    row.selected = checked;
    this.cdr.markForCheck();
  }

  canCopyLookupsToRight(): boolean {
    const section = this.sections.lookups;
    return (
      section.leftLookups.some((r) => r.selected) &&
      !section.lookupSyncing &&
      !section.loading &&
      !this.lookupUpdatePreview &&
      !this.isInterfaceLocked(this.rightInterface)
    );
  }

  canCopyLookupsToLeft(): boolean {
    const section = this.sections.lookups;
    return (
      section.rightLookups.some((r) => r.selected) &&
      !section.lookupSyncing &&
      !section.loading &&
      !this.lookupUpdatePreview &&
      !this.isInterfaceLocked(this.leftInterface)
    );
  }

  copyLookupsToRight(): void {
    this.copyLookupsBetweenSides('left', 'right');
  }

  copyLookupsToLeft(): void {
    this.copyLookupsBetweenSides('right', 'left');
  }

  cancelLookupUpdatePreview(): void {
    this.lookupUpdatePreview = null;
    this.cdr.markForCheck();
  }

  confirmLookupUpdatePreview(): void {
    const preview = this.lookupUpdatePreview;
    if (!preview) {
      return;
    }
    this.lookupUpdatePreview = null;
    void this.executeLookupUpdates(preview.sourceSide, preview.targetSide, preview.pairs);
  }

  formatLookupRowLabel(row: CompareLookupRow): string {
    return `${row.lookupType} | ${row.lookupId} | ${row.lookupValue}`;
  }

  /** Selected rows in display order (selected block at top). */
  private getSelectedLookupsInDisplayOrder(side: ComparePaneSide): CompareLookupRow[] {
    return this.getDisplayLookups(side).filter((r) => r.selected);
  }

  private copyLookupsBetweenSides(
    sourceSide: ComparePaneSide,
    targetSide: ComparePaneSide,
  ): void {
    const section = this.sections.lookups;
    const targetIface = targetSide === 'left' ? this.leftInterface : this.rightInterface;
    const sourceSelected = this.getSelectedLookupsInDisplayOrder(sourceSide);
    const targetSelected = this.getSelectedLookupsInDisplayOrder(targetSide);

    if (!sourceSelected.length) {
      this.toastr.warning('Select at least one lookup row to copy.', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }
    if (this.isInterfaceLocked(targetIface)) {
      this.toastr.warning(
        `${targetSide === 'left' ? 'Left' : 'Right'} interface is locked.`,
        '',
        { timeOut: 4000, positionClass: 'toast-bottom-right' },
      );
      return;
    }

    // Both sides selected → update by position (equal count required).
    if (targetSelected.length > 0) {
      if (sourceSelected.length !== targetSelected.length) {
        this.toastr.warning(
          'Equal number of rows needs to be selected to update.',
          '',
          { timeOut: 4500, positionClass: 'toast-bottom-right' },
        );
        return;
      }
      this.lookupUpdatePreview = {
        sourceSide,
        targetSide,
        pairs: sourceSelected.map((source, i) => ({
          source,
          target: targetSelected[i],
        })),
      };
      this.cdr.markForCheck();
      return;
    }

    // Source only → insert
    void this.executeLookupInserts(sourceSide, targetSide, sourceSelected);
  }

  private async executeLookupInserts(
    sourceSide: ComparePaneSide,
    targetSide: ComparePaneSide,
    sourceSelected: CompareLookupRow[],
  ): Promise<void> {
    const section = this.sections.lookups;
    const targetRows =
      targetSide === 'left' ? section.leftLookups : section.rightLookups;
    const targetIface = targetSide === 'left' ? this.leftInterface : this.rightInterface;
    const targetUniqueId = targetIface?.UniqueID;

    section.lookupSyncing = true;
    this.cdr.markForCheck();

    let addedCount = 0;
    let skippedCount = 0;
    const failures: string[] = [];

    for (const sourceRow of sourceSelected) {
      if (!this.isLookupRowComplete(sourceRow)) {
        failures.push(
          `Skipped incomplete row (${sourceRow.lookupType}/${sourceRow.lookupId}).`,
        );
        continue;
      }

      if (
        this.findLookupByFullKey(
          targetRows,
          sourceRow.lookupType,
          sourceRow.lookupId,
          sourceRow.lookupValue,
        )
      ) {
        skippedCount++;
        continue;
      }

      const payload = {
        id: 0,
        LookupType: sourceRow.lookupType,
        LookupId: sourceRow.lookupId,
        LookupValue: sourceRow.lookupValue,
        Handler: targetIface?.id ?? '',
      };

      try {
        const response = await this.service.postapi('api/XMLMappings/CreateUpdate', payload);
        if (response?.['status'] && response?.['statusText']) {
          failures.push(
            `${sourceRow.lookupType}/${sourceRow.lookupId}: save failed.`,
          );
          continue;
        }
        const res = response as any;
        if (res == null || String(res?.['id'] ?? res?.Id ?? '') === '-1') {
          failures.push(
            `${sourceRow.lookupType}/${sourceRow.lookupId}: already exists.`,
          );
          continue;
        }
        const savedId = Number(res?.['id'] ?? res?.Id ?? 0) || 0;
        targetRows.push({
          id: savedId,
          contextId: String(targetUniqueId ?? ''),
          lookupType: sourceRow.lookupType,
          lookupId: sourceRow.lookupId,
          lookupValue: sourceRow.lookupValue,
          selected: false,
        });
        this.sortLookupRows(targetRows);
        addedCount++;
      } catch {
        failures.push(
          `${sourceRow.lookupType}/${sourceRow.lookupId}: save failed.`,
        );
      }
    }

    await this.finishLookupSync({
      sourceSide,
      targetSide,
      targetIface,
      selectedCount: sourceSelected.length,
      updatedCount: 0,
      addedCount,
      skippedCount,
      failures,
    });
  }

  private async executeLookupUpdates(
    sourceSide: ComparePaneSide,
    targetSide: ComparePaneSide,
    pairs: Array<{ source: CompareLookupRow; target: CompareLookupRow }>,
  ): Promise<void> {
    const section = this.sections.lookups;
    const targetRows =
      targetSide === 'left' ? section.leftLookups : section.rightLookups;
    const targetIface = targetSide === 'left' ? this.leftInterface : this.rightInterface;

    section.lookupSyncing = true;
    this.cdr.markForCheck();

    let updatedCount = 0;
    let skippedCount = 0;
    const failures: string[] = [];

    for (const { source: sourceRow, target: targetRow } of pairs) {
      if (!this.isLookupRowComplete(sourceRow)) {
        failures.push(
          `Skipped incomplete row (${sourceRow.lookupType}/${sourceRow.lookupId}).`,
        );
        continue;
      }

      const sameAsTarget =
        this.lookupFullMatchKey(
          sourceRow.lookupType,
          sourceRow.lookupId,
          sourceRow.lookupValue,
        ) ===
        this.lookupFullMatchKey(
          targetRow.lookupType,
          targetRow.lookupId,
          targetRow.lookupValue,
        );
      if (sameAsTarget) {
        skippedCount++;
        continue;
      }

      const duplicateElsewhere = this.findLookupByFullKey(
        targetRows,
        sourceRow.lookupType,
        sourceRow.lookupId,
        sourceRow.lookupValue,
        targetRow.id,
      );
      if (duplicateElsewhere) {
        failures.push(
          `${sourceRow.lookupType}/${sourceRow.lookupId}/${sourceRow.lookupValue}: duplicate exists on target.`,
        );
        continue;
      }

      if (!targetRow.id) {
        failures.push(
          `${targetRow.lookupType}/${targetRow.lookupId}: cannot update row without id.`,
        );
        continue;
      }

      const payload = {
        id: targetRow.id,
        LookupType: sourceRow.lookupType,
        LookupId: sourceRow.lookupId,
        LookupValue: sourceRow.lookupValue,
        Handler: targetIface?.id ?? '',
      };

      try {
        const response = await this.service.postapi('api/XMLMappings/CreateUpdate', payload);
        if (response?.['status'] && response?.['statusText']) {
          failures.push(
            `${sourceRow.lookupType}/${sourceRow.lookupId}: save failed.`,
          );
          continue;
        }
        const res = response as any;
        if (res == null || String(res?.['id'] ?? res?.Id ?? '') === '-1') {
          failures.push(
            `${sourceRow.lookupType}/${sourceRow.lookupId}: already exists.`,
          );
          continue;
        }
        targetRow.lookupType = sourceRow.lookupType;
        targetRow.lookupId = sourceRow.lookupId;
        targetRow.lookupValue = sourceRow.lookupValue;
        updatedCount++;
      } catch {
        failures.push(
          `${sourceRow.lookupType}/${sourceRow.lookupId}: save failed.`,
        );
      }
    }

    await this.finishLookupSync({
      sourceSide,
      targetSide,
      targetIface,
      selectedCount: pairs.length,
      updatedCount,
      addedCount: 0,
      skippedCount,
      failures,
    });
  }

  private async finishLookupSync(args: {
    sourceSide: ComparePaneSide;
    targetSide: ComparePaneSide;
    targetIface: any;
    selectedCount: number;
    updatedCount: number;
    addedCount: number;
    skippedCount: number;
    failures: string[];
  }): Promise<void> {
    const section = this.sections.lookups;
    await this.reloadAllLookups();
    this.clearLookupSelections(section);
    this.lookupUpdatePreview = null;
    section.lookupSyncing = false;

    const sourceLabel = args.sourceSide === 'left' ? 'Left' : 'Right';
    const targetLabel = args.targetSide === 'left' ? 'Left' : 'Right';
    const summary = `${args.updatedCount} updated, ${args.addedCount} added`;
    const auditMessage = `Compare ${sourceLabel} to ${targetLabel} Look ups: ${summary}${
      args.skippedCount > 0 ? `, ${args.skippedCount} skipped` : ''
    } (${args.selectedCount} selected)`;

    if (args.updatedCount > 0 || args.addedCount > 0) {
      const actionType =
        args.addedCount > 0 && args.updatedCount === 0 ? 'Create' : 'Update';
      this.recordPipelineSaveAudit(
        args.targetIface?.UniqueID,
        actionType,
        auditMessage,
        'Lookup sync completed successfully',
      );
      let successMsg = `Look ups copied to ${targetLabel}: ${summary}.`;
      if (args.skippedCount > 0) {
        successMsg += ` ${args.skippedCount} skipped (already exist on target).`;
      }
      this.toastr.success(successMsg, '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
    }

    if (args.failures.length) {
      this.service.copyableToastError(args.failures.join('\n'));
    } else if (
      args.skippedCount > 0 &&
      args.updatedCount === 0 &&
      args.addedCount === 0
    ) {
      this.toastr.info('Already exists on target — nothing to copy.', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
    } else if (
      args.updatedCount === 0 &&
      args.addedCount === 0 &&
      args.skippedCount === 0
    ) {
      this.toastr.info('No lookups were copied.', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
    }

    this.cdr.markForCheck();
  }

  private isLookupRowComplete(row: CompareLookupRow): boolean {
    return (
      !!String(row.lookupType ?? '').trim() &&
      !!String(row.lookupId ?? '').trim() &&
      !!String(row.lookupValue ?? '').trim()
    );
  }

  private findTemplateLookupOnSide(
    rows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
  ): CompareLookupRow | undefined {
    const key = this.lookupMatchKey(lookupType, lookupId);
    return rows.find(
      (r) =>
        String(r.contextId ?? '').trim() === '-1' &&
        this.lookupMatchKey(r.lookupType, r.lookupId) === key,
    );
  }

  private findLookupByFullKey(
    rows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
    lookupValue: string,
    excludeId?: number,
  ): CompareLookupRow | undefined {
    const key = this.lookupFullMatchKey(lookupType, lookupId, lookupValue);
    return rows.find((r) => {
      if (excludeId != null && excludeId > 0 && r.id === excludeId) {
        return false;
      }
      return (
        this.lookupFullMatchKey(r.lookupType, r.lookupId, r.lookupValue) === key
      );
    });
  }

  private findLookupByKey(
    rows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
  ): CompareLookupRow | undefined {
    const key = this.lookupMatchKey(lookupType, lookupId);
    return rows.find((r) => this.lookupMatchKey(r.lookupType, r.lookupId) === key);
  }

  /** Match only pipeline-owned rows; ignore shared template rows (ContextId = -1). */
  private findPipelineLookupOnSide(
    rows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
    pipelineUniqueId: string | number | undefined,
  ): CompareLookupRow | undefined {
    const key = this.lookupMatchKey(lookupType, lookupId);
    return rows.find(
      (r) =>
        this.isPipelineLookupRow(r, pipelineUniqueId) &&
        this.lookupMatchKey(r.lookupType, r.lookupId) === key,
    );
  }

  private isPipelineLookupRow(
    row: CompareLookupRow,
    pipelineUniqueId: string | number | undefined,
  ): boolean {
    if (pipelineUniqueId === undefined || pipelineUniqueId === null || pipelineUniqueId === '') {
      return false;
    }
    const rowContextId = String(row.contextId ?? '').trim();
    if (!rowContextId || rowContextId === '-1') {
      return false;
    }
    return this.contextIdsEqual(rowContextId, pipelineUniqueId);
  }

  private contextIdsEqual(
    a: string | number | undefined,
    b: string | number | undefined,
  ): boolean {
    const sa = String(a ?? '').trim();
    const sb = String(b ?? '').trim();
    if (!sa || !sb) {
      return false;
    }
    if (sa === sb) {
      return true;
    }
    const na = Number(sa);
    const nb = Number(sb);
    return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
  }

  private lookupMatchKey(lookupType: string, lookupId: string): string {
    return `${String(lookupType ?? '').trim()}|${String(lookupId ?? '').trim()}`.toLowerCase();
  }

  private lookupFullMatchKey(
    lookupType: string,
    lookupId: string,
    lookupValue: string,
  ): string {
    return `${String(lookupType ?? '').trim()}|${String(lookupId ?? '').trim()}|${String(lookupValue ?? '').trim()}`.toLowerCase();
  }

  private sortLookupRows(rows: CompareLookupRow[]): void {
    rows.sort((a, b) =>
      `${a.lookupType}|${a.lookupId}`.localeCompare(`${b.lookupType}|${b.lookupId}`),
    );
  }

  private clearLookupSelections(section: CompareSectionState): void {
    section.lookupSelectionSide = null;
    section.leftLookups.forEach((r) => (r.selected = false));
    section.rightLookups.forEach((r) => (r.selected = false));
  }

  private async reloadAllLookups(): Promise<void> {
    await Promise.all([
      this.reloadLookupsForSide('left'),
      this.reloadLookupsForSide('right'),
    ]);
  }

  private async reloadLookupsForSide(side: ComparePaneSide): Promise<void> {
    const section = this.sections.lookups;
    const iface = side === 'left' ? this.leftInterface : this.rightInterface;
    const body = { ContextId: iface.UniqueID, Handler: '' };
    try {
      const data = await this.service.postapi('api/XMLMappings/GetByHandler', body);
      const rows = this.normalizeLookups(data);
      if (side === 'left') {
        section.leftLookups = rows;
      } else {
        section.rightLookups = rows;
      }
    } catch {
      // Keep existing rows on reload failure.
    }
  }

  private normalizeLookups(data: any): CompareLookupRow[] {
    const list = Array.isArray(data) ? data : [];
    return list
      .map((row) => ({
        id: Number(row.Id ?? row.id ?? 0) || 0,
        contextId: String(row.ContextId ?? row.contextId ?? ''),
        lookupType: row.LookupType || row.lookupType || '',
        lookupId: row.LookupId || row.lookupId || '',
        lookupValue: row.LookupValue || row.lookupValue || '',
        selected: false,
      }))
      .sort((a, b) =>
        `${a.lookupType}|${a.lookupId}`.localeCompare(`${b.lookupType}|${b.lookupId}`),
      );
  }

  private formatXml(xml: string): string {
    if (!xml) {
      return '';
    }
    const cleaned = this.stripNodeIdentifierFromXml(xml);
    if (cleaned.length > 250000) {
      return cleaned;
    }
    try {
      return this.stripNodeIdentifierFromXml(beautify(cleaned));
    } catch {
      return cleaned;
    }
  }

  /** Display-only: remove internal GUID attrs/elements (raw API XML kept for save). */
  private stripNodeIdentifierFromXml(xml: string): string {
    return xml
      .replace(/\s+_NodeIdentifier\s*=\s*(?:"[^"]*"|'[^']*')/gi, '')
      .replace(/_NodeIdentifier\s*=\s*(?:"[^"]*"|'[^']*')/gi, '')
      .replace(/<\s*_NodeIdentifier\b[^>]*\/>/gi, '')
      .replace(/<\s*_NodeIdentifier\b[^>]*>[\s\S]*?<\/\s*_NodeIdentifier\s*>/gi, '');
  }

  private isMappingLoadError(value: unknown): value is MappingLoadError {
    return (
      typeof value === 'object' &&
      value !== null &&
      'error' in value &&
      typeof (value as MappingLoadError).error === 'string'
    );
  }

  private isMappingLoadResult(value: unknown): value is MappingLoadResult {
    return (
      typeof value === 'object' &&
      value !== null &&
      'displayXml' in value &&
      'rawXml' in value &&
      'meta' in value
    );
  }

  private resolveRuleAuditLabel(stage: RuleExecutionStage): string {
    if (stage === 'Delivered') {
      return 'Predelivery Rule Config';
    }
    if (stage === 'PreValidation') {
      return 'PreValidation Rule Config';
    }
    return 'RuleConfig';
  }

  private validateValidationPaneContent(
    side: ComparePaneSide,
    content: string,
    mode: ValidationMappingMode,
  ): string | null {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    const trimmed = (content ?? '').trim();

    if (mode === 'rules') {
      if (this.looksLikeXml(trimmed) && !this.canParseJson(trimmed)) {
        return `${sideLabel} pane must contain pre-validation rule JSON, not XML.`;
      }
      if (!this.canParseJson(trimmed || '[]')) {
        return `${sideLabel} pane contains invalid pre-validation rule JSON.`;
      }
      return null;
    }

    if (mode === 'none') {
      if (!trimmed) {
        return `${sideLabel} pane is empty; paste pre-validation rule JSON (or legacy XML) to create a mapping.`;
      }
      if (this.canParseJson(trimmed) && !this.looksLikeXml(trimmed)) {
        return null;
      }
      if (this.looksLikeXml(trimmed)) {
        return null;
      }
      return `${sideLabel} pane must contain pre-validation rule JSON or legacy validation XML to create a mapping.`;
    }

    if (this.looksLikeJson(trimmed) && !this.looksLikeXml(trimmed)) {
      return `${sideLabel} pane must contain legacy validation mapping XML, not JSON.`;
    }
    return null;
  }

  private looksLikeJson(text: string): boolean {
    if (!text) {
      return false;
    }
    const first = text.charAt(0);
    return (first === '{' || first === '[') && this.canParseJson(text);
  }

  private looksLikeXml(text: string): boolean {
    if (!text) {
      return false;
    }
    const trimmed = text.trimStart();
    return trimmed.startsWith('<?xml') || trimmed.startsWith('<');
  }

  private canParseJson(text: string): boolean {
    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  }

  private errorMessage(err: unknown): string {
    if (typeof err === 'string') {
      return err;
    }
    if (err instanceof Error) {
      return err.message;
    }
    if (typeof err === 'object' && err !== null && 'message' in err) {
      const message = (err as { message?: unknown }).message;
      if (typeof message === 'string') {
        return message;
      }
    }
    return 'Load failed';
  }

  close(): void {
    this.activeModal.dismiss();
  }

  copyToClip(value: string, label: string): void {
    if (value && typeof copyToClipboard === 'function') {
      copyToClipboard(value);
      this.toastr.success(label + ' copied', '', {
        timeOut: 2000,
        positionClass: 'toast-bottom-right',
      });
    }
  }
}

declare const copyToClipboard: (content: string) => void;
